import { createStudyRecord } from './schema';
import { createId, isoNow } from './crypto';
import type { IndexedDbStudyRepository } from './repository';
import type {
  AttachmentMetadata,
  AttachmentSyncPullResult,
  OutboxEntry,
  StoredAttachment,
  StudyRecord,
  SyncPullResult,
  SyncPushResult,
  SyncRunResult,
} from './types';

export interface RecordSyncChange {
  opId: string;
  operation: 'record-upsert' | 'record-delete';
  baseRevision: number;
  record: StudyRecord;
}

export function compactRecordChanges(changes: RecordSyncChange[]): RecordSyncChange[] {
  const latestByEntity = new Map<string, RecordSyncChange>();
  for (const change of changes) {
    const key = `${change.record.type}\u0000${change.record.id}`;
    const current = latestByEntity.get(key);
    if (!current
      || change.record.revision > current.record.revision
      || (change.record.revision === current.record.revision
        && change.record.updatedAt >= current.record.updatedAt)) {
      latestByEntity.set(key, {
        ...change,
        baseRevision: Math.min(current?.baseRevision ?? change.baseRevision, change.baseRevision),
      });
    }
  }
  return [...latestByEntity.values()];
}

export interface AttachmentSyncChange {
  opId: string;
  operation: 'attachment-upsert' | 'attachment-delete';
  baseRevision: number;
  metadata: AttachmentMetadata;
  blob?: Blob;
}

export interface SyncAdapter {
  readonly kind: 'offline' | 'supabase';
  readonly configured: boolean;
  pushRecords(changes: RecordSyncChange[]): Promise<SyncPushResult>;
  pullRecords(cursor?: string): Promise<SyncPullResult>;
  pushAttachment(change: AttachmentSyncChange): Promise<SyncPushResult>;
  pullAttachments(cursor?: string): Promise<AttachmentSyncPullResult>;
}

export class OfflineSyncAdapter implements SyncAdapter {
  readonly kind = 'offline' as const;
  readonly configured = false;

  constructor(readonly reason = '未配置云同步，继续使用本机离线数据') {}

  async pushRecords(): Promise<SyncPushResult> {
    return { outcomes: [] };
  }

  async pullRecords(cursor?: string): Promise<SyncPullResult> {
    return { records: [], cursor };
  }

  async pushAttachment(): Promise<SyncPushResult> {
    // Offline is an intentional mode. Keep outbox entries pending for a future configured adapter.
    return { outcomes: [] };
  }

  async pullAttachments(cursor?: string): Promise<AttachmentSyncPullResult> {
    return { attachments: [], cursor };
  }
}

const metadataOnly = (attachment: StoredAttachment): AttachmentMetadata => {
  const { blob: _blob, ...metadata } = attachment;
  return metadata;
};

function asRecordChange(entry: OutboxEntry): RecordSyncChange | undefined {
  if (entry.operation !== 'record-upsert' && entry.operation !== 'record-delete') return undefined;
  if (!entry.payload || typeof entry.payload !== 'object') return undefined;
  return {
    opId: entry.id,
    operation: entry.operation,
    baseRevision: entry.baseRevision,
    record: entry.payload as StudyRecord,
  };
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right));
  return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(',')}}`;
}

function recordsEquivalent(left: StudyRecord, right: StudyRecord): boolean {
  return left.type === right.type
    && left.id === right.id
    && left.revision === right.revision
    && left.studyDate === right.studyDate
    && left.deletedAt === right.deletedAt
    && canonicalJson(left.payload) === canonicalJson(right.payload);
}

function attachmentsEquivalent(left: AttachmentMetadata, right: AttachmentMetadata): boolean {
  return left.id === right.id
    && left.revision === right.revision
    && left.sha256 === right.sha256
    && left.byteSize === right.byteSize
    && left.ownerType === right.ownerType
    && left.ownerId === right.ownerId
    && left.fileName === right.fileName
    && left.deletedAt === right.deletedAt;
}

async function saveRecordConflict(
  repository: IndexedDbStudyRepository,
  event: string,
  local: StudyRecord,
  remote: unknown,
  remoteRevision?: number,
): Promise<void> {
  const deviceId = await repository.getDeviceId();
  await repository.saveRecord(createStudyRecord({
    type: 'activity-event',
    deviceId,
    source: 'remote',
    studyDate: local.studyDate,
    payload: {
      event,
      entityKey: local.key,
      localRevision: local.revision,
      remoteRevision,
      localRecord: local,
      remoteRecord: remote,
    },
  }), { queueSync: false });
}

async function saveAttachmentConflict(
  repository: IndexedDbStudyRepository,
  event: string,
  local: StoredAttachment | undefined,
  submitted: AttachmentMetadata,
  remote: unknown,
  remoteRevision?: number,
): Promise<void> {
  const deviceId = await repository.getDeviceId();
  let forkAttachmentId: string | undefined;
  if (local?.blob) {
    const now = isoNow();
    forkAttachmentId = createId('attachment-conflict');
    await repository.saveAttachment({
      ...local,
      id: forkAttachmentId,
      fileName: `${local.fileName}（同步冲突副本）`,
      revision: 1,
      createdAt: now,
      updatedAt: now,
      deletedAt: undefined,
      remoteObjectKey: undefined,
    }, { queueSync: false, preserveRevision: true });
  }
  await repository.saveRecord(createStudyRecord({
    type: 'activity-event',
    deviceId,
    source: 'remote',
    payload: {
      event,
      entityKey: `attachment:${submitted.id}`,
      localRevision: submitted.revision,
      remoteRevision,
      localMetadata: submitted,
      remoteMetadata: remote,
      forkAttachmentId,
    },
  }), { queueSync: false });
}

export async function runSync(
  repository: IndexedDbStudyRepository,
  adapter: SyncAdapter,
): Promise<SyncRunResult> {
  const pending = await repository.listOutbox();
  if (!adapter.configured) {
    return {
      mode: 'offline',
      pushed: 0,
      pulled: 0,
      conflicts: 0,
      pending: pending.length,
      errors: [],
    };
  }

  const errors: string[] = [];
  let pushed = 0;
  let conflicts = 0;
  const recordChanges = pending.map(asRecordChange).filter((value): value is RecordSyncChange => Boolean(value));
  if (recordChanges.length) {
    try {
      const result = await adapter.pushRecords(recordChanges);
      const submitted = new Map(recordChanges.map((change) => [change.opId, change]));
      const seen = new Set<string>();
      for (const outcome of result.outcomes) {
        const change = submitted.get(outcome.opId);
        if (!change) {
          errors.push(`同步适配器返回未知记录操作：${outcome.opId}`);
          continue;
        }
        if (seen.has(outcome.opId)) {
          errors.push(`同步适配器重复返回记录操作：${outcome.opId}`);
          continue;
        }
        seen.add(outcome.opId);
        if (outcome.status === 'accepted') {
          pushed += 1;
          await repository.settleAcceptedOutbox(
            change.opId,
            change.record.key,
            change.baseRevision,
            outcome.remoteRevision,
          );
        } else if (outcome.status === 'conflict') {
          conflicts += 1;
          await saveRecordConflict(
            repository,
            'sync-push-conflict',
            change.record,
            outcome.remote,
            outcome.remoteRevision,
          );
          await repository.settleResolvedOutbox(change.opId, change.record.key);
        } else {
          errors.push(outcome.message);
          await repository.markOutboxFailure(change.opId, outcome.message);
        }
      }
      for (const change of recordChanges) {
        if (seen.has(change.opId)) continue;
        const message = `同步适配器未返回记录操作结果：${change.opId}`;
        errors.push(message);
        await repository.markOutboxFailure(change.opId, message);
      }
    } catch (error) {
      errors.push(error instanceof Error ? error.message : String(error));
      for (const change of recordChanges) await repository.markOutboxFailure(change.opId, errors.at(-1) ?? '同步失败');
    }
  }

  for (const entry of pending) {
    if (entry.operation !== 'attachment-upsert' && entry.operation !== 'attachment-delete') continue;
    try {
      const id = entry.entityKey.replace(/^attachment:/, '');
      const attachment = await repository.getAttachment(id);
      const metadata = entry.payload as AttachmentMetadata;
      if (!metadata || metadata.id !== id) throw new Error(`附件队列 ${entry.id} 的元数据无效`);
      if (entry.operation === 'attachment-upsert' && (
        !attachment
        || attachment.revision !== metadata.revision
        || attachment.sha256 !== metadata.sha256
      )) {
        throw new Error(`附件 ${id} 已在同步期间更新，旧操作保持待处理`);
      }
      const result = await adapter.pushAttachment({
        opId: entry.id,
        operation: entry.operation,
        baseRevision: entry.baseRevision,
        metadata,
        blob: entry.operation === 'attachment-upsert' ? attachment?.blob : undefined,
      });
      if (result.outcomes.length !== 1 || result.outcomes[0]?.opId !== entry.id) {
        throw new Error(`同步适配器未唯一返回附件操作结果：${entry.id}`);
      }
      const [outcome] = result.outcomes;
      if (outcome.status === 'accepted') {
        pushed += 1;
        await repository.settleAcceptedOutbox(
          entry.id,
          entry.entityKey,
          entry.baseRevision,
          outcome.remoteRevision,
        );
      } else if (outcome.status === 'conflict') {
        conflicts += 1;
        await saveAttachmentConflict(
          repository,
          'attachment-sync-push-conflict',
          attachment,
          metadata,
          outcome.remote,
          outcome.remoteRevision,
        );
        await repository.settleResolvedOutbox(entry.id, entry.entityKey);
      } else {
        errors.push(outcome.message);
        await repository.markOutboxFailure(entry.id, outcome.message);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      errors.push(message);
      await repository.markOutboxFailure(entry.id, message);
    }
  }

  let pulled = 0;
  let cursor = await repository.getSetting<string>('sync:cursor');
  try {
    const result = await adapter.pullRecords(cursor);
    const stillPending = new Map((await repository.listOutbox()).map((entry) => [entry.entityKey, entry]));
    for (const remote of result.records) {
      const local = await repository.getRecordByKey(remote.key);
      const pendingEntry = stillPending.get(remote.key);
      if (pendingEntry) {
        if (local && (
          remote.revision > pendingEntry.baseRevision
          || (remote.revision === local.revision && !recordsEquivalent(local, remote))
        )) {
          conflicts += 1;
          await saveRecordConflict(repository, 'sync-pull-conflict', local, remote, remote.revision);
        }
        continue;
      }
      if (!local || remote.revision > local.revision) {
        await repository.saveRecord({ ...remote, source: 'remote' }, { queueSync: false, preserveRevision: true });
        pulled += 1;
      } else if (remote.revision === local.revision && !recordsEquivalent(local, remote)) {
        conflicts += 1;
        await saveRecordConflict(repository, 'sync-pull-fork', local, remote, remote.revision);
        await repository.saveRecord({ ...remote, source: 'remote' }, { queueSync: false, preserveRevision: true });
        pulled += 1;
      }
    }
    cursor = result.cursor ?? cursor;
    if (cursor) await repository.setSetting('sync:cursor', cursor);
  } catch (error) {
    errors.push(error instanceof Error ? error.message : String(error));
  }

  let attachmentCursor = await repository.getSetting<string>('sync:attachment-cursor');
  try {
    const result = await adapter.pullAttachments(attachmentCursor);
    const stillPending = new Map((await repository.listOutbox()).map((entry) => [entry.entityKey, entry]));
    for (const failure of result.failures ?? []) {
      errors.push(failure.message);
      const deviceId = await repository.getDeviceId();
      await repository.saveRecord(createStudyRecord({
        type: 'activity-event',
        deviceId,
        source: 'remote',
        payload: {
          event: 'attachment-sync-integrity-failure',
          entityKey: failure.entityKey,
          remoteRevision: failure.remoteRevision,
          message: failure.message,
        },
      }), { queueSync: false });
    }
    for (const remote of result.attachments) {
      const key = `attachment:${remote.metadata.id}`;
      const local = await repository.getAttachment(remote.metadata.id);
      const pendingEntry = stillPending.get(key);
      if (pendingEntry) {
        if (local && (
          remote.metadata.revision > pendingEntry.baseRevision
          || (remote.metadata.revision === local.revision
            && !attachmentsEquivalent(metadataOnly(local), remote.metadata))
        )) {
          conflicts += 1;
          await saveAttachmentConflict(
            repository,
            'attachment-sync-pull-conflict',
            local,
            metadataOnly(local),
            remote.metadata,
            remote.metadata.revision,
          );
        }
        continue;
      }
      if (remote.metadata.deletedAt) {
        if (local && remote.metadata.revision > local.revision) {
          await repository.saveAttachment(
            { ...local, ...remote.metadata, blob: local.blob },
            { queueSync: false, preserveRevision: true },
          );
          pulled += 1;
        } else if (local && remote.metadata.revision === local.revision
          && !attachmentsEquivalent(metadataOnly(local), remote.metadata)) {
          conflicts += 1;
          await saveAttachmentConflict(
            repository,
            'attachment-sync-pull-fork',
            local,
            metadataOnly(local),
            remote.metadata,
            remote.metadata.revision,
          );
          await repository.saveAttachment(
            { ...local, ...remote.metadata, blob: local.blob },
            { queueSync: false, preserveRevision: true },
          );
          pulled += 1;
        }
        continue;
      }
      if (!remote.blob) continue;
      if (!local || remote.metadata.revision > local.revision) {
        await repository.saveAttachment(
          { ...remote.metadata, blob: remote.blob },
          { queueSync: false, preserveRevision: true },
        );
        pulled += 1;
      } else if (remote.metadata.revision === local.revision
        && !attachmentsEquivalent(metadataOnly(local), remote.metadata)) {
        conflicts += 1;
        await saveAttachmentConflict(
          repository,
          'attachment-sync-pull-fork',
          local,
          metadataOnly(local),
          remote.metadata,
          remote.metadata.revision,
        );
        await repository.saveAttachment(
          { ...remote.metadata, blob: remote.blob },
          { queueSync: false, preserveRevision: true },
        );
        pulled += 1;
      }
    }
    attachmentCursor = result.cursor ?? attachmentCursor;
    if (attachmentCursor) await repository.setSetting('sync:attachment-cursor', attachmentCursor);
  } catch (error) {
    errors.push(error instanceof Error ? error.message : String(error));
  }

  const remaining = (await repository.listOutbox()).length;
  return {
    mode: errors.length || conflicts || remaining ? 'partial' : 'synced',
    pushed,
    pulled,
    conflicts,
    pending: remaining,
    cursor,
    errors,
  };
}
